import { notification } from 'antd';
import {
  QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
} from 'react-query';

import { Carrier } from '../../models/Carrier';
import { Option } from '../../models/Option';
import { Valuehistory } from '../../models/Valuehistory';
import carrierService from '../../services/carrierService';

export const surchargeHistoryQueryKey = (carrierId: number) =>
  ['surchargeHistory', carrierId] as const;

export const useSurchargeHistory = (carrierId: number, enabled: boolean) =>
  useQuery<Valuehistory[]>(
    surchargeHistoryQueryKey(carrierId),
    () => carrierService.getSurchargeHistory(carrierId),
    { enabled: enabled && !!carrierId }
  );

export type FuelSurchargeUpdateValues = {
  carrierId: number;
  fscExpiryEmailEnabled: boolean;
  fuelSurcharge: number;
  fuelSurchargeExpiry: string | null;
};

export type FuelSurchargeUpdateResult = {
  succeededIds: number[];
};

const fscFields = (values: FuelSurchargeUpdateValues) => ({
  fscExpiryEmailEnabled: values.fscExpiryEmailEnabled,
  fuelSurcharge: values.fuelSurcharge,
  fuelSurchargeExpiry: values.fuelSurchargeExpiry,
});

const mergeFuelSurchargeOption = (
  values: FuelSurchargeUpdateValues,
  existing: Option | null
): Option =>
  ({
    ...(existing || {}),
    ...fscFields(values),
    carrierId: values.carrierId,
  } as Option);

const patchFuelSurchargeCaches = (
  queryClient: QueryClient,
  option: Option,
  carrierId: number
) => {
  const lastSurchargeChange = new Date().toISOString();

  queryClient.setQueryData<Option | null>(['carrierOption', carrierId], option);

  const cachedCarrier = queryClient.getQueryData<Carrier>([
    'carrier',
    carrierId,
  ]);
  if (cachedCarrier) {
    queryClient.setQueryData<Carrier>(['carrier', carrierId], {
      ...cachedCarrier,
      last_surcharge_change: lastSurchargeChange,
      option,
    });
  }

  const cachedCarriers = queryClient.getQueryData<Carrier[]>('allCarriers');
  if (cachedCarriers) {
    queryClient.setQueryData<Carrier[]>(
      'allCarriers',
      cachedCarriers.map((carrier) =>
        carrier.id === carrierId
          ? {
              ...carrier,
              last_surcharge_change: lastSurchargeChange,
              option,
            }
          : carrier
      )
    );
  }
};

const notifyFuelSurchargeResult = (
  total: number,
  succeededIds: number[],
  firstError?: Error
) => {
  const failed = total - succeededIds.length;

  if (total === 1) {
    if (failed === 0) {
      notification.success({
        description: 'Changes saved successfully.',
        message: 'Success',
      });
      return;
    }
    notification.error({
      description: firstError?.message || 'Failed to save changes.',
      message: 'Error',
    });
    return;
  }

  if (failed === 0) {
    notification.success({
      description: `Updated ${succeededIds.length} of ${total}`,
      message: 'Success',
    });
    return;
  }

  notification.error({
    description: `Updated ${succeededIds.length} of ${total}; ${failed} failed`,
    message: 'Error',
  });
};

export const useOptionUpdate = ({
  notify = true,
}: { notify?: boolean } = {}) => {
  const queryClient = useQueryClient();

  return useMutation<Option, Error, Option>(carrierService.updateOption, {
    onError: (error) => {
      if (!notify) return;
      notification.error({
        description: error.message || 'Failed to save changes.',
        message: 'Error',
      });
    },
    onSuccess: (data) => {
      queryClient.setQueryData<Option | null>(
        ['carrierOption', data.carrierId],
        data
      );
      queryClient.setQueryData<Carrier | undefined>(
        ['carrier', data.carrierId],
        (carrier?: Carrier) =>
          carrier && {
            ...carrier,
            option: data,
          }
      );
      if (!notify) return;
      notification.success({
        description: 'Changes saved successfully.',
        message: 'Success',
      });
    },
  });
};

export const useFuelSurchargeUpdate = () => {
  const queryClient = useQueryClient();

  return useMutation<
    FuelSurchargeUpdateResult,
    Error,
    FuelSurchargeUpdateValues | FuelSurchargeUpdateValues[]
  >(async (input) => {
    const items = Array.isArray(input) ? input : [input];

    const results = await Promise.allSettled(
      items.map(async (values) => {
        const existing = await carrierService.getOption(values.carrierId);
        const option = await carrierService.updateOption(
          mergeFuelSurchargeOption(values, existing)
        );
        patchFuelSurchargeCaches(queryClient, option, values.carrierId);
        queryClient.invalidateQueries(
          surchargeHistoryQueryKey(values.carrierId)
        );
        return option;
      })
    );

    const succeededIds = items
      .filter((_, index) => results[index].status === 'fulfilled')
      .map((item) => item.carrierId);
    const firstRejection = results.find(
      (result): result is PromiseRejectedResult => result.status === 'rejected'
    );
    const firstError =
      firstRejection?.reason instanceof Error
        ? firstRejection.reason
        : undefined;

    notifyFuelSurchargeResult(items.length, succeededIds, firstError);

    if (succeededIds.length === 0) {
      throw firstError || new Error('Failed to update fuel surcharge.');
    }

    return { succeededIds };
  });
};
